import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, Card, Table, Tag, Button, Space, 
  Modal, message, Badge, Tooltip, Empty,
  Row, Col, Statistic, Input, Form, Avatar
} from 'antd';
import { 
  CheckCircleOutlined, CloseCircleOutlined, WalletOutlined,
  ReloadOutlined, ExclamationCircleOutlined, ArrowLeftOutlined,
  UserOutlined, TransactionOutlined, GlobalOutlined, CalendarOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { Title, Text } = Typography;

const WithdrawalManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getWithdrawals, processWithdrawal, loading } = useAdmin();
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const loadWithdrawals = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getWithdrawals({ status: 'requested' });
      if (result.success) {
        setWithdrawals(result.data || []);
      }
    } catch (err) {
      message.error(t('manager.withdrawals.messages.load_error'));
    } finally {
      setIsLoading(false);
    }
  }, [getWithdrawals, t]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  const handleProcess = async (withdrawalId, status) => {
    Modal.confirm({
      title: status === 'admin_approved' ? t('manager.withdrawals.modal.approve_title') : t('manager.withdrawals.modal.reject_title'),
      className: 'dark-modal',
      content: (
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="admin_note" label={t('manager.withdrawals.form.note')}>
            <Input.TextArea 
              rows={3} 
              placeholder={t('manager.withdrawals.form.note_placeholder')} 
              className="bg-slate-900 border-white/10 text-white"
            />
          </Form.Item>
        </Form>
      ),
      okText: t('manager.withdrawals.actions.confirm'),
      cancelText: t('manager.withdrawals.actions.cancel'),
      onOk: async () => {
        const values = form.getFieldsValue();
        try {
          const res = await processWithdrawal(withdrawalId, { 
            action: status === 'admin_approved' ? 'approve' : 'reject', 
            notes: values.admin_note 
          });
          if (res.success) {
            message.success(t('manager.withdrawals.messages.process_success'));
            loadWithdrawals();
            form.resetFields();
          } else {
            message.error(res.message || t('common.error'));
          }
        } catch (err) {
          message.error(t('common.error'));
        }
      }
    });
  };

  const columns = [
    {
      title: t('manager.withdrawals.table.user'),
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
            <UserOutlined />
          </Avatar>
          <Space direction="vertical" size={0}>
            <Text className="text-white font-bold text-xs">{record.username}</Text>
            <Text className="text-slate-500 text-[10px]">{record.email}</Text>
          </Space>
        </Space>
      )
    },
    {
      title: t('manager.withdrawals.table.amount'),
      key: 'amount',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text className="text-white font-bold text-xs">{record.amount} {record.symbol || record.asset}</Text>
          <Text className="text-slate-500 text-[10px]">{t('manager.withdrawals.table.net_amount')}: {record.net_amount || record.amount} ({t('manager.withdrawals.table.fee')}: {record.fee || '0.00'})</Text>
        </Space>
      )
    },
    {
      title: t('manager.withdrawals.table.destination'),
      key: 'destination',
      render: (_, record) => (
        <Tooltip title={record.address || record.to_address}>
          <Space direction="vertical" size={0}>
            <Text ellipsis className="text-indigo-400 text-[10px] font-mono" style={{ maxWidth: 120 }}>{record.address || record.to_address}</Text>
            <Tag className="m-0 border-none bg-slate-800 text-slate-400 text-[9px] font-bold uppercase">{record.network || record.chain || 'CRYPTO'}</Tag>
          </Space>
        </Tooltip>
      )
    },
    {
      title: t('manager.withdrawals.table.date'),
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => (
        <Space className="text-slate-400 text-xs">
          <CalendarOutlined className="text-slate-600" />
          {moment(date).format('DD/MM/YYYY HH:mm')}
        </Space>
      )
    },
    {
      title: t('manager.withdrawals.table.status'),
      key: 'status',
      render: () => <Tag color="warning" className="m-0 text-[10px] font-black uppercase tracking-widest bg-orange-500/10 text-orange-400 border-orange-500/20">PENDING</Tag>
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<CheckCircleOutlined />} 
            onClick={() => handleProcess(record.id, 'admin_approved')}
            className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20 font-bold text-xs"
          >
            {t('manager.withdrawals.actions.approve')}
          </Button>
          <Button 
            danger 
            icon={<CloseCircleOutlined />} 
            onClick={() => handleProcess(record.id, 'rejected')}
            className="bg-rose-500/10 text-rose-500 border-rose-500/20 hover:bg-rose-500/20 font-bold text-xs"
          >
            {t('manager.withdrawals.actions.reject')}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-950 text-slate-200 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <Space direction="vertical" size={0}>
          <Space align="center" className="mb-1">
            <Button 
              icon={<ArrowLeftOutlined />} 
              type="link" 
              onClick={() => navigate('/manager')}
              className="text-slate-500 hover:text-white p-0 mr-2"
            />
            <div className="w-1.5 h-6 bg-orange-500 rounded-full" />
            <Title level={2} className="m-0 !text-white !font-black uppercase tracking-tight">
              {t('manager.withdrawals.title')}
            </Title>
          </Space>
          <Text className="text-slate-500 font-medium tracking-wide">
            {t('manager.withdrawals.subtitle')}
          </Text>
        </Space>
        
        <Space>
          <Card className="trading-card border-none py-1 px-4 bg-white/5">
            <Statistic 
              title={<Text className="text-slate-500 text-[10px] uppercase font-black">{t('manager.withdrawals.stats.to_process')}</Text>}
              value={withdrawals.length} 
              valueStyle={{ color: '#f43f5e', fontSize: '1.2rem', fontWeight: '900' }}
            />
          </Card>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadWithdrawals} 
            loading={isLoading}
            className="btn-pro bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            {t('manager.dashboard.refresh')}
          </Button>
        </Space>
      </div>

      <Card className="trading-card border-none overflow-hidden">
        <Table 
          columns={columns} 
          dataSource={withdrawals} 
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          className="custom-table"
          locale={{ 
            emptyText: (
              <div className="py-12">
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<Text className="text-slate-500 font-bold uppercase tracking-widest text-xs">{t('manager.withdrawals.empty')}</Text>} 
                />
              </div>
            )
          }}
        />
      </Card>

      <style jsx="true">{`
        .custom-table .ant-table {
          background: transparent !important;
        }
        .custom-table .ant-table-thead > tr > th {
          background: transparent !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #475569 !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.1em !important;
          font-weight: 900 !important;
          padding: 16px !important;
        }
        .custom-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
          padding: 16px !important;
        }
        .custom-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.02) !important;
        }
        .ant-pagination-item, .ant-pagination-prev, .ant-pagination-next {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .ant-pagination-item-active {
          border-color: #6366f1 !important;
          background: #6366f1 !important;
        }
        .ant-pagination-item-active a {
          color: white !important;
        }
        .ant-pagination-item a {
          color: #94a3b8 !important;
        }
      `}</style>
    </div>
  );
};

export default WithdrawalManagement;

